import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';

import { ApiRequestError, api } from '../lib/api.js';
import { Alert, Button, Card, Field, StatusBadge, TextInput } from './ui.js';

interface TotpStatus {
  enabled: boolean;
  pending: boolean;
  recoveryLeft: number;
}

interface Enrollment {
  secret: string;
  otpauthUrl: string;
}

/**
 * Cadastro do segundo fator.
 *
 * Vive num componente proprio e nao dentro da tela de configuracoes porque ele
 * tem tres estados com telas diferentes — desligado, cadastrando e ligado — e
 * um deles (os codigos de recuperacao) so acontece uma vez na vida da conta.
 *
 * Mora em `components/` e nao em `pages/admin/` de proposito: hoje so a equipe
 * Azko tem onde ativar, mas nada aqui e especifico do backoffice. Quando o
 * sindico ganhar a mesma opcao, e so montar o mesmo cartao na tela dele.
 */
export function TwoFactorCard() {
  const queryClient = useQueryClient();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [desativando, setDesativando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useQuery({
    queryKey: ['auth', '2fa'],
    queryFn: () => api.get<TotpStatus>('/auth/2fa'),
  });

  function fail(cause: unknown) {
    setError(cause instanceof ApiRequestError ? cause.message : 'Nao consegui completar a acao.');
  }

  const iniciar = useMutation({
    mutationFn: () => api.post<Enrollment>('/auth/2fa/iniciar'),
    onSuccess: async (data) => {
      setError(null);
      setEnrollment(data);
      // O QR e gerado NO NAVEGADOR a partir da otpauth: o segredo nao passa por
      // servico de terceiro para virar imagem — que e o erro classico de quem
      // usa uma API de QR na internet para isto.
      setQr(await QRCode.toDataURL(data.otpauthUrl, { margin: 1, width: 220 }));
    },
    onError: fail,
  });

  const confirmar = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/2fa/confirmar', { code }),
    onSuccess: (data) => {
      setError(null);
      setEnrollment(null);
      setQr(null);
      setCode('');
      setRecoveryCodes(data.recoveryCodes);
      void queryClient.invalidateQueries({ queryKey: ['auth', '2fa'] });
    },
    onError: fail,
  });

  const desativar = useMutation({
    mutationFn: () => api.post('/auth/2fa/desativar', { code }),
    onSuccess: () => {
      setError(null);
      setCode('');
      setDesativando(false);
      void queryClient.invalidateQueries({ queryKey: ['auth', '2fa'] });
    },
    onError: fail,
  });

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">Segundo fator</p>
        {status?.enabled ? (
          <StatusBadge tone="accent">Ativo</StatusBadge>
        ) : (
          <StatusBadge tone="warn">Desligado</StatusBadge>
        )}
      </div>

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {recoveryCodes ? (
        <Recovery codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      ) : status?.enabled ? (
        <Ativo
          recoveryLeft={status.recoveryLeft}
          desativando={desativando}
          code={code}
          setCode={setCode}
          onAskDisable={() => setDesativando(true)}
          onCancel={() => {
            setDesativando(false);
            setCode('');
          }}
          onConfirm={() => desativar.mutate()}
          working={desativar.isPending}
        />
      ) : enrollment ? (
        <Cadastro
          enrollment={enrollment}
          qr={qr}
          code={code}
          setCode={setCode}
          onConfirm={() => confirmar.mutate()}
          working={confirmar.isPending}
        />
      ) : (
        <Desligado onStart={() => iniciar.mutate()} working={iniciar.isPending} />
      )}
    </Card>
  );
}

function Desligado({ onStart, working }: { onStart: () => void; working: boolean }) {
  return (
    <>
      <p className="mt-3 text-sm leading-relaxed text-muted">
        Uma senha e tudo o que separa alguem do controle de todos os condominios:
        aprovar conta, cortar acesso, reemitir a credencial de um sindico. Com o
        segundo fator, a senha sozinha deixa de bastar.
      </p>
      <Button className="mt-4" loading={working} onClick={onStart}>
        Ativar segundo fator
      </Button>
    </>
  );
}

function Cadastro({
  enrollment,
  qr,
  code,
  setCode,
  onConfirm,
  working,
}: {
  enrollment: Enrollment;
  qr: string | null;
  code: string;
  setCode: (value: string) => void;
  onConfirm: () => void;
  working: boolean;
}) {
  return (
    <>
      <p className="mt-3 text-sm leading-relaxed text-muted">
        Leia o codigo abaixo no Google Authenticator, no 1Password ou em qualquer
        aplicativo de autenticacao. Depois digite os seis digitos que ele mostrar.
      </p>

      {qr && (
        // Fundo branco: o QR e preto sobre transparente e some no tema escuro.
        <img
          src={qr}
          alt="QR Code do segundo fator"
          className="mt-4 rounded-xl bg-white p-3"
          width={220}
          height={220}
        />
      )}

      <p className="mt-3 text-xs text-faint">
        Sem camera? Digite este codigo no aplicativo:
      </p>
      {/* `break-all` porque sao 32 caracteres sem espaco e o cartao tem 22rem. */}
      <code className="mt-1 block break-all rounded-xl bg-surface-2 p-3 text-xs tracking-wider">
        {enrollment.secret}
      </code>

      <form
        className="mt-4 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm();
        }}
      >
        <Field label="Codigo do aplicativo">
          <TextInput
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={10}
            autoFocus
            required
          />
        </Field>
        <Button type="submit" className="w-full" loading={working} disabled={code.length < 6}>
          Confirmar e ativar
        </Button>
      </form>
    </>
  );
}

/**
 * Os codigos de recuperacao, mostrados uma unica vez.
 *
 * O banco guarda so o SHA-256 deles: nem a Azko consegue mostrar de novo. Por
 * isso a tela nao tem "fechar" discreto — o botao diz o que a pessoa esta
 * afirmando ao clicar.
 */
function Recovery({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <>
      <p className="mt-3 text-sm font-bold text-fg">Guarde estes codigos agora.</p>
      <p className="mt-1 text-sm leading-relaxed text-muted">
        Cada um funciona UMA vez, no lugar do aplicativo. Sao eles que devolvem o
        acesso se o telefone quebrar ou for trocado. Eles nao aparecem de novo:
        o sistema guarda so o hash.
      </p>

      <ul className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-surface-2 p-3">
        {codes.map((code) => (
          <li key={code} className="font-mono text-sm tracking-wider">
            {code}
          </li>
        ))}
      </ul>

      <Button className="mt-4 w-full" onClick={onDone}>
        Guardei os codigos
      </Button>
    </>
  );
}

function Ativo({
  recoveryLeft,
  desativando,
  code,
  setCode,
  onAskDisable,
  onCancel,
  onConfirm,
  working,
}: {
  recoveryLeft: number;
  desativando: boolean;
  code: string;
  setCode: (value: string) => void;
  onAskDisable: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  working: boolean;
}) {
  if (desativando) {
    return (
      <form
        className="mt-4 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm();
        }}
      >
        {/* O codigo e exigido mesmo com a sessao aberta, pelo mesmo motivo que
            a troca de senha pede a senha atual: aparelho destravado no balcao
            nao pode desligar a protecao da conta em dois cliques. */}
        <Field label="Confirme com um codigo para desativar">
          <TextInput
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={20}
            autoFocus
            required
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={working} disabled={code.length < 6}>
            Desativar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Voltar
          </Button>
        </div>
      </form>
    );
  }

  return (
    <>
      <p className="mt-3 text-sm leading-relaxed text-muted">
        O login desta conta pede um codigo do aplicativo. Restam{' '}
        <span className="font-bold text-fg">{recoveryLeft}</span>{' '}
        {recoveryLeft === 1 ? 'codigo de recuperacao' : 'codigos de recuperacao'}.
      </p>

      {recoveryLeft <= 2 && (
        <div className="mt-3">
          <Alert tone="warn">
            Restam poucos codigos de recuperacao. Desative e ative o segundo fator
            para gerar dez novos — e guarde-os.
          </Alert>
        </div>
      )}

      <Button variant="ghost" className="mt-4" onClick={onAskDisable}>
        Desativar
      </Button>
    </>
  );
}
